import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination
} from '@mui/material';

import { Add, Search, Edit, Delete, Close } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../config/api';
import './BudgetCodeForm.css';

const BudgetCode = () => {
  const { user } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    budgetCode: '',
    groupName: '',
    custName: '',
    deptName: '',
    address1: '',
    address2: '',
    address3: '',
    custType: 'L'
  });
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dialogMessage, setDialogMessage] = useState({ text: '', type: '' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    businessGroups: [],
    customers: [],
    departments: []
  });
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewDeptInput, setShowNewDeptInput] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [showNewCustInput, setShowNewCustInput] = useState(false);
  const [newCustName, setNewCustName] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  useEffect(() => {
    fetchBudgetCodes();
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BUDGET_CODES}/lists`);
      const data = await response.json();
      setDropdownData(data);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const fetchBudgetCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.BUDGET_CODES);
      const data = await response.json();
      const formattedData = data.map(item => ({
        id: item.budget_code_id,
        budgetCode: item.budget_code,
        groupName: item.group_name,
        custName: item.cust_name,
        deptName: item.dept_name,
        address1: item.address_1,
        address2: item.address_2,
        address3: item.address_3,
        custType: item.cust_type,
        modifiedDt: item.modified_dt
      }));
      setAllRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      setMessage({ text: 'Failed to load budget codes', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const rowToEdit = filteredRows.find(row => row.id === id);
    setFormData({
      budgetCode: rowToEdit.budgetCode,
      groupName: rowToEdit.groupName,
      custName: rowToEdit.custName,
      deptName: rowToEdit.deptName,
      address1: rowToEdit.address1 || '',
      address2: rowToEdit.address2 || '',
      address3: rowToEdit.address3 || '',
      custType: rowToEdit.custType
    });
    setEditMode(true);
    setEditId(id);
    setOpen(true);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredRows(allRows);
      return;
    }

    const filtered = allRows.filter(row => {
      const searchValue = searchTerm.toLowerCase();
      return (
        (row.budgetCode && row.budgetCode.toLowerCase().includes(searchValue)) ||
        (row.groupName && row.groupName.toLowerCase().includes(searchValue)) ||
        (row.custName && row.custName.toLowerCase().includes(searchValue)) ||
        (row.deptName && row.deptName.toLowerCase().includes(searchValue))
      );
    });
    setFilteredRows(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.budgetCode.trim()) newErrors.budgetCode = true;
    if (!formData.groupName.trim()) newErrors.groupName = true;
    if (!formData.custName.trim()) newErrors.custName = true;
    if (!formData.deptName.trim()) newErrors.deptName = true;
    if (!formData.address2.trim()) newErrors.address2 = true;
    if (!formData.address3.trim()) newErrors.address3 = true;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    setDialogMessage({ text: '', type: '' });

    if (!validateForm()) {
      setDialogMessage({ text: 'Please fill all required fields', type: 'error' });
      return;
    }

    setSaveLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const url = editMode ? `${API_ENDPOINTS.BUDGET_CODES}/${editId}` : API_ENDPOINTS.BUDGET_CODES;
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          },
          modifiedDt: editMode ? filteredRows.find(row => row.id === editId)?.modifiedDt : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${editMode ? 'update' : 'create'} budget code`);
      }

      await fetchBudgetCodes();
      await fetchDropdownData();
      setOpen(false);
      handleClear();
      setMessage({ text: `Budget code ${editMode ? 'updated' : 'created'} successfully`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setDialogMessage({ text: error.message, type: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      budgetCode: '',
      groupName: '',
      custName: '',
      deptName: '',
      address1: '',
      address2: '',
      address3: '',
      custType: 'L'
    });
    setErrors({});
    setDialogMessage({ text: '', type: '' });
    setEditMode(false);
    setEditId(null);
    setShowNewGroupInput(false);
    setNewGroupName('');
    setShowNewDeptInput(false);
    setNewDeptName('');
    setShowNewCustInput(false);
    setNewCustName('');
  };

  const handleDeleteClick = () => {
    if (!selectedRows || selectedRows.size === 0) {
      setMessage({ text: 'Please select items to delete', type: 'warning' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSingle = (id) => {
    setDeleteRowId(id);
    setSingleDeleteConfirmOpen(true);
  };

  const handleSingleDeleteConfirm = async () => {
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(`${API_ENDPOINTS.BUDGET_CODES}/${deleteRowId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete budget code');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      await fetchBudgetCodes();
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete budget code', type: 'error' });
      setSingleDeleteConfirmOpen(false);
      setDeleteRowId(null);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const selectedIds = Array.from(selectedRows);
    setDeleteLoading(true);
    const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');

    try {
      const response = await fetch(API_ENDPOINTS.BUDGET_CODES, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectionModel: selectedIds,
          user: {
            username: storedUser?.username || user?.username || 'system',
            id: storedUser?.userId || storedUser?.id || user?.userId || user?.id || 1
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete budget codes');
      }

      const result = await response.json();
      setMessage({ text: result.message, type: 'success' });
      setSelectedRows(new Set());
      await fetchBudgetCodes();
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } catch (error) {
      setMessage({ text: error.message || 'Failed to delete budget codes', type: 'error' });
      setDeleteConfirmOpen(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Budget Code Administration</Typography>

      {(loading || saveLoading || deleteLoading) && <LinearProgress sx={{ mb: 2 }} />}
      
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} role="alert" aria-live="polite">
          {message.text}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            inputProps={{ 'aria-label': 'Search budget codes' }}
          />
          <Button variant="outlined" startIcon={<Search />} onClick={handleSearch} sx={{ minWidth: '44px', minHeight: '44px' }}>
            Search
          </Button>
          {filteredRows.length !== allRows.length && (
            <Button variant="outlined" onClick={() => { setFilteredRows(allRows); setSearchTerm(''); }} sx={{ minWidth: '44px', minHeight: '44px' }}>
              Show All
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />} 
            onClick={handleDeleteClick}
            disabled={!selectedRows || selectedRows.size === 0 || deleteLoading}
            sx={{ minWidth: '44px', minHeight: '44px' }}
            aria-label={`Delete ${selectedRows?.size || 0} selected budget codes`}
          >
            Delete ({selectedRows?.size || 0})
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditMode(false); setEditId(null); setOpen(true); }} sx={{ minWidth: '44px', minHeight: '44px' }}>
            Add Budget Code
          </Button>
        </Box>
      </Box>

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell padding="checkbox" sx={{ backgroundColor: '#0d47a1' }}>
                    <Checkbox
                      color="default"
                      indeterminate={selectedRows.size > 0 && filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).some(row => selectedRows.has(row.id)) && !filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).every(row => selectedRows.has(row.id))}
                      checked={filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).length > 0 && filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).every(row => selectedRows.has(row.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const pageIds = filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).map(row => row.id);
                          const allIds = new Set([...selectedRows, ...pageIds]);
                          setSelectedRows(allIds);
                        } else {
                          const pageIds = filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).map(row => row.id);
                          const newSelected = new Set(selectedRows);
                          pageIds.forEach(id => newSelected.delete(id));
                          setSelectedRows(newSelected);
                        }
                      }}
                      inputProps={{ 'aria-label': 'Select all budget codes on this page' }}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' }, '&:focus-visible': { outline: '2px solid white', outlineOffset: '2px' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1', padding: '10px 12px' }}>
                    Budget Code
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1', padding: '10px 12px' }}>
                    Business Group
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1', padding: '10px 12px' }}>
                    Customer Name
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1', padding: '10px 12px' }}>
                    Department
                  </TableCell>
                  <TableCell sx={{ color: 'white !important', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#0d47a1', padding: '10px 12px' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.slice(paginationModel.page * paginationModel.pageSize, paginationModel.page * paginationModel.pageSize + paginationModel.pageSize).map((row, index) => {
                  const isItemSelected = selectedRows.has(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#e3f2fd !important' }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onChange={(e) => {
                            const newSelected = new Set(selectedRows);
                            if (e.target.checked) {
                              newSelected.add(row.id);
                            } else {
                              newSelected.delete(row.id);
                            }
                            setSelectedRows(newSelected);
                          }}
                          inputProps={{ 'aria-labelledby': labelId }}
                          sx={{ '&:focus-visible': { outline: '2px solid #1976d2', outlineOffset: '2px' } }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        sx={{ fontSize: '13px', padding: '6px 12px' }}
                      >
                        {row.budgetCode || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                        {row.groupName || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                        {row.custName || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                        {row.deptName || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            onClick={() => handleEdit(row.id)}
                            aria-label={`Edit budget code ${row.budgetCode}`}
                            sx={{ minWidth: '44px', minHeight: '44px', padding: '10px' }}
                          >
                            <Edit sx={{ color: 'primary.main' }} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteSingle(row.id)}
                            aria-label={`Delete budget code ${row.budgetCode}`}
                            sx={{ minWidth: '44px', minHeight: '44px', padding: '10px' }}
                          >
                            <Delete sx={{ color: 'error.main' }} />
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={(event, newPage) => setPaginationModel({ ...paginationModel, page: newPage })}
            onRowsPerPageChange={(event) => setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) })}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}

      <Dialog open={open} maxWidth="lg" fullWidth aria-labelledby="dialog-title">
        <DialogTitle id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editMode ? 'Edit Budget Code' : 'Add Budget Code'}
          <Button onClick={() => { setOpen(false); handleClear(); }} aria-label="Close dialog" sx={{ minWidth: '44px', minHeight: '44px' }}>
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent className="budget-form-container">
          {dialogMessage.text && (
            <Alert severity={dialogMessage.type} sx={{ mb: 2 }} role="alert" aria-live="polite">
              {dialogMessage.text}
            </Alert>
          )}
          <div className="budget-form">
            {/* Row 1: Business Group and Budget Code */}
            <div className="form-row-dual">
              <div className="form-field">
                {!showNewGroupInput ? (
                  <FormControl fullWidth error={errors.groupName}>
                    <InputLabel id="business-group-label">Business Group *</InputLabel>
                    <Select
                      labelId="business-group-label"
                      value={formData.groupName}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW__') {
                          setShowNewGroupInput(true);
                          setFormData({ ...formData, groupName: '' });
                        } else {
                          setFormData({ ...formData, groupName: e.target.value });
                        }
                      }}
                      label="Business Group *"
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      inputProps={{ 'aria-describedby': errors.groupName ? 'business-group-error' : undefined }}
                    >
                      <MenuItem value="">---Select One---</MenuItem>
                      <MenuItem value="__ADD_NEW__" sx={{ fontStyle: 'italic', color: 'primary.main' }}>+ Add New Business Group</MenuItem>
                      {(dropdownData.businessGroups || []).map((group) => (
                        <MenuItem key={group} value={group}>{group}</MenuItem>
                      ))}
                    </Select>
                    {errors.groupName && <Typography id="business-group-error" variant="caption" color="error" sx={{ ml: 2 }} role="alert">Business group is required</Typography>}
                  </FormControl>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="New Business Group *"
                      value={newGroupName}
                      onChange={(e) => {
                        setNewGroupName(e.target.value);
                        setFormData({ ...formData, groupName: e.target.value });
                      }}
                      error={errors.groupName}
                      helperText={errors.groupName ? 'Business group is required' : ''}
                      placeholder="Enter new business group name"
                      inputProps={{ maxLength: 100 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowNewGroupInput(false);
                        setNewGroupName('');
                        setFormData({ ...formData, groupName: '' });
                      }}
                      sx={{ mt: 1, minWidth: '80px', minHeight: '40px' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </div>
              <div className="form-field">
                <TextField
                  fullWidth
                  label="Budget Code /Affiliate # (Company Code) *"
                  value={formData.budgetCode}
                  onChange={(e) => setFormData({ ...formData, budgetCode: e.target.value })}
                  error={errors.budgetCode}
                  helperText={errors.budgetCode ? 'Budget code is required' : ''}
                  placeholder="Enter budget code"
                  inputProps={{ maxLength: 32, 'aria-describedby': errors.budgetCode ? 'budget-code-helper-text' : undefined }}
                  FormHelperTextProps={{ id: 'budget-code-helper-text', role: 'alert' }}
                />
              </div>
            </div>
            
            {/* Row 2: Customer Name and Department */}
            <div className="form-row-dual">
              <div className="form-field">
                {!showNewCustInput ? (
                  <FormControl fullWidth error={errors.custName}>
                    <InputLabel id="customer-name-label">Customer Name *</InputLabel>
                    <Select
                      labelId="customer-name-label"
                      value={formData.custName}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW_CUST__') {
                          setShowNewCustInput(true);
                          setFormData({ ...formData, custName: '' });
                        } else {
                          setFormData({ ...formData, custName: e.target.value });
                        }
                      }}
                      label="Customer Name *"
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      inputProps={{ 'aria-describedby': errors.custName ? 'customer-name-error' : undefined }}
                    >
                      <MenuItem value="">---Select One---</MenuItem>
                      <MenuItem value="__ADD_NEW_CUST__" sx={{ fontStyle: 'italic', color: 'primary.main' }}>+ Add New Customer Name</MenuItem>
                      {(dropdownData.customers || []).map((customer) => (
                        <MenuItem key={customer} value={customer}>{customer}</MenuItem>
                      ))}
                    </Select>
                    {errors.custName && <Typography id="customer-name-error" variant="caption" color="error" sx={{ ml: 2 }} role="alert">Customer name is required</Typography>}
                  </FormControl>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="New Customer Name *"
                      value={newCustName}
                      onChange={(e) => {
                        setNewCustName(e.target.value);
                        setFormData({ ...formData, custName: e.target.value });
                      }}
                      error={errors.custName}
                      helperText={errors.custName ? 'Customer name is required' : ''}
                      placeholder="Enter new customer name"
                      inputProps={{ maxLength: 100 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowNewCustInput(false);
                        setNewCustName('');
                        setFormData({ ...formData, custName: '' });
                      }}
                      sx={{ mt: 1, minWidth: '80px', minHeight: '40px' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </div>
              <div className="form-field">
                {!showNewDeptInput ? (
                  <FormControl fullWidth error={errors.deptName}>
                    <InputLabel id="department-label">Department *</InputLabel>
                    <Select
                      labelId="department-label"
                      value={formData.deptName}
                      onChange={(e) => {
                        if (e.target.value === '__ADD_NEW_DEPT__') {
                          setShowNewDeptInput(true);
                          setFormData({ ...formData, deptName: '' });
                        } else {
                          setFormData({ ...formData, deptName: e.target.value });
                        }
                      }}
                      label="Department *"
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      inputProps={{ 'aria-describedby': errors.deptName ? 'department-error' : undefined }}
                    >
                      <MenuItem value="">---Select One---</MenuItem>
                      <MenuItem value="__ADD_NEW_DEPT__" sx={{ fontStyle: 'italic', color: 'primary.main' }}>+ Add New Department</MenuItem>
                      {(dropdownData.departments || []).map((dept) => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                    {errors.deptName && <Typography id="department-error" variant="caption" color="error" sx={{ ml: 2 }} role="alert">Department is required</Typography>}
                  </FormControl>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label="New Department *"
                      value={newDeptName}
                      onChange={(e) => {
                        setNewDeptName(e.target.value);
                        setFormData({ ...formData, deptName: e.target.value });
                      }}
                      error={errors.deptName}
                      helperText={errors.deptName ? 'Department is required' : ''}
                      placeholder="Enter new department name"
                      inputProps={{ maxLength: 100 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowNewDeptInput(false);
                        setNewDeptName('');
                        setFormData({ ...formData, deptName: '' });
                      }}
                      sx={{ mt: 1, minWidth: '80px', minHeight: '40px' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </div>
            </div>
            
            {/* Row 3: Business Name and Street Address */}
            <div className="form-row-dual">
              <div className="form-field">
                <label className="form-label" htmlFor="business-name">Business Name</label>
                <TextField
                  id="business-name"
                  fullWidth
                  value={formData.address1}
                  onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
                  placeholder="Enter business name"
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 50, 'aria-label': 'Business Name' }}
                />
              </div>
              <div className="form-field">
                <label className="form-label required" htmlFor="street-address">Street Address</label>
                <TextField
                  id="street-address"
                  fullWidth
                  value={formData.address2}
                  onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                  error={errors.address2}
                  helperText={errors.address2 ? 'Street address is required' : ''}
                  placeholder="Enter street address"
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 50, 'aria-describedby': errors.address2 ? 'street-address-helper-text' : undefined }}
                  FormHelperTextProps={{ id: 'street-address-helper-text', role: 'alert' }}
                />
              </div>
            </div>
            
            {/* Row 5: City, State, Zipcode and Customer Type */}
            <div className="form-row-dual">
              <div className="form-field" style={{flex: '2'}}>
                <label className="form-label required" htmlFor="city-state-zip">City, State, Zipcode</label>
                <TextField
                  id="city-state-zip"
                  fullWidth
                  value={formData.address3}
                  onChange={(e) => setFormData({ ...formData, address3: e.target.value })}
                  error={errors.address3}
                  helperText={errors.address3 ? 'City, State, Zipcode is required' : ''}
                  placeholder="Enter city, state, zipcode"
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 50, 'aria-describedby': errors.address3 ? 'city-state-zip-helper-text' : undefined }}
                  FormHelperTextProps={{ id: 'city-state-zip-helper-text', role: 'alert' }}
                />
              </div>
              <div className="form-field" style={{flex: '1'}}>
                <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                  <legend className="form-label required">Customer Type</legend>
                  <div className="radio-group" role="radiogroup" aria-label="Customer Type">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="customerType"
                        value="L"
                        checked={formData.custType === 'L'}
                        onChange={(e) => setFormData({ ...formData, custType: e.target.value })}
                        aria-label="Local"
                      />
                      Local
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="customerType"
                        value="O"
                        checked={formData.custType === 'O'}
                        onChange={(e) => setFormData({ ...formData, custType: e.target.value })}
                        aria-label="Overseas"
                      />
                      Overseas
                    </label>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} disabled={saveLoading} sx={{ minWidth: '44px', minHeight: '44px' }}>Clear</Button>
          <Button onClick={() => { setOpen(false); handleClear(); }} disabled={saveLoading} sx={{ minWidth: '44px', minHeight: '44px' }}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
            sx={{ minWidth: '44px', minHeight: '44px' }}
          >
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} aria-labelledby="delete-confirm-title">
        <DialogTitle id="delete-confirm-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedRows?.size || 0} selected budget code{(selectedRows?.size || 0) > 1 ? 's' : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading} sx={{ minWidth: '44px', minHeight: '44px' }}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
            sx={{ minWidth: '44px', minHeight: '44px' }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={singleDeleteConfirmOpen} onClose={() => setSingleDeleteConfirmOpen(false)} aria-labelledby="single-delete-confirm-title">
        <DialogTitle id="single-delete-confirm-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this budget code?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSingleDeleteConfirmOpen(false)} disabled={deleteLoading} sx={{ minWidth: '44px', minHeight: '44px' }}>Cancel</Button>
          <Button 
            onClick={handleSingleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
            sx={{ minWidth: '44px', minHeight: '44px' }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetCode;